import _ from 'lodash';

import {
  IVScrollItem,
  IBaseVirtualScrollProps,
  IVScrollItemResize,
  IVScrollUpdateOptions,
} from '../types';

const DEFAULT_BUFFER_SIZE = 2;
const DEFAULT_ITEM_SIZE = 96;

/**
 * Keeps state of virtual scroll, based on scroll position,
 * item resize events and item list changes.
 * Also keeps 'scrollTop' for the active item (if present) on items resize.
 * Real viewport - size of the element on screen that contains items.
 */

/* eslint-disable-next-line */
export function useVirtualScroll(arg: IBaseVirtualScrollProps) {
  const buffer = arg.buffer ?? DEFAULT_BUFFER_SIZE;
  const dataKey = arg.dataKey;
  const defaultItemSize = arg.defaultItemSize ?? DEFAULT_ITEM_SIZE;
  let activeItemId = arg.activeItemId;

  let viewportSize = 0;
  let headerSlotSize = 0;
  let footerSlotSize = 0;

  let scrollTop = 0;
  const itemSizesById = {} as Record<number | string, number>;
  let items = arg.items;

  function getActiveItemIndex() {
    if (activeItemId) {
      const activeItem = items.find(v => v[dataKey] === activeItemId);
      return items.indexOf(activeItem as IVScrollItem);
    }
  }

  const getOffsetTop = () => {
    const isHeaderSlotVisible = scrollTop <= headerSlotSize;
    if (isHeaderSlotVisible) {
      return {
        isHeaderSlotVisible,
        offsetTopItems: 0,
        offsetTopDistance: 0,
        offsetTopItemsBuffer: 0,
        offsetTopDistanceBuffer: 0,
      };
    }

    let offsetTopItems = 0;
    /* Add header height to the cumulative height of items when it is hidden */
    let offsetTopDistance = headerSlotSize;
    /* Store items' sizes for later backtracking */
    const nLastSizes: number[] = [];

    /* Iterate over items' heights, while they are fully above viewport */
    for (;;) {
      const item = items[offsetTopItems];
      if (!item) {
        break;
      }

      const size = getItemSize(item);
      if (offsetTopDistance + size >= scrollTop) {
        break;
      }

      offsetTopItems++;
      offsetTopDistance += size;
      nLastSizes.push(size);
    }

    /* Iterate up to "buffer" items back to determine offset values for virtual viewport */
    const bufferTop = Math.min(offsetTopItems, buffer);
    const offsetTopItemsBuffer = offsetTopItems - bufferTop;
    let offsetTopDistanceBuffer = offsetTopDistance;

    for (let i = 0; i < bufferTop; i++) {
      const size = nLastSizes[nLastSizes.length - 1 - i];
      offsetTopDistanceBuffer -= size;
    }

    return {
      isHeaderSlotVisible,
      offsetTopItems,
      offsetTopDistance,
      offsetTopItemsBuffer,
      offsetTopDistanceBuffer,
    };
  };

  /** Calculate how many items, at least partially, will fit on user's screen */
  const getNItemsInViewport = () => {
    const offsetTop = getOffsetTop();
    let offsetIndex = offsetTop.offsetTopItems;
    /* First visible item can only partially fit on screen,
       so we increase available area by this hidden part. */
    let sizeInViewport = offsetTop.offsetTopDistance - scrollTop;

    do {
      const item = items[offsetIndex];
      /* This is possible when footer is present */
      if (!item) {
        break;
      }

      sizeInViewport += getItemSize(item);
      offsetIndex++;
    } while (sizeInViewport < viewportSize);

    return offsetIndex - offsetTop.offsetTopItems;
  };

  /**
   * Calculate how many items are available after last item that fits inside viewport,
   * as well as bottom offset for virtual viewport.
   */
  const getOffsetBottom = () => {
    const offsetTop = getOffsetTop();
    const nItemsInViewport = getNItemsInViewport();
    /* How many items are above viewport + at least partially inside viewport */
    const offsetBottomIndex = offsetTop.offsetTopItems
      + nItemsInViewport;
    const offsetBottomItems = items.length - offsetBottomIndex;

    let offsetBottomDistance = 0;
    const nLastSizes: number[] = [];

    /* Add sizes of the remaining items */
    for (let i = offsetBottomIndex; i < items.length; i++) {
      const item = items[i];
      const size = getItemSize(item);

      offsetBottomDistance += size;
      nLastSizes.push(size);
    }

    /* Iterate up to "buffer" items back, to include them in virtual viewport */
    const bufferBottom = Math.min(offsetBottomItems, buffer);
    const offsetBottomItemsBuffer = offsetBottomItems - bufferBottom;
    let offsetBottomDistanceBuffer = offsetBottomDistance;

    for (let i = 0; i < bufferBottom; i++) {
      const size = nLastSizes[nLastSizes.length - 1 - i];
      offsetBottomDistanceBuffer -= size;
    }

    const isFooterSlotVisible = offsetBottomItemsBuffer === 0;

    /* Add height of a hidden footer as an additional padding */
    if (!isFooterSlotVisible) {
      offsetBottomDistance += footerSlotSize;
      offsetBottomDistanceBuffer += footerSlotSize;
    }

    return {
      isFooterSlotVisible,
      offsetBottomItems,
      offsetBottomDistance,
      offsetBottomItemsBuffer,
      offsetBottomDistanceBuffer,
    };
  };

  /** How many items should be rendered, including buffer items outside viewport */
  const getNItemsInViewportWithBuffer = () => {
    const offsetTop = getOffsetTop();
    const nItemsInViewport = getNItemsInViewport();
    const offsetBottom = getOffsetBottom();
    const bufferTop = offsetTop.offsetTopItems
      - offsetTop.offsetTopItemsBuffer;
    const bufferBottom = offsetBottom.offsetBottomItems
      - offsetBottom.offsetBottomItemsBuffer;
    return nItemsInViewport + bufferTop + bufferBottom;
  };

  function getScrollTopDiff(resize: IVScrollItemResize) {
    if (arg.unpinActiveItem) {
      return 0;
    }
    let result = 0;
    if (activeItemId) {
      const { id, size } = resize!;
      const bufferTopItems = getBufferTopItems();
      const isFromBufferTop = bufferTopItems.find(item => item[dataKey] === id);
      if (!isFromBufferTop) {
        const item = items.find(item => item[dataKey] === id);
        const itemIndex = items.indexOf(item!);
        const activeItemIndex = getActiveItemIndex();
        const isAboveActiveItem = itemIndex < activeItemIndex!;
        const oldSize = itemSizesById[id];
        if (isAboveActiveItem && Number.isFinite(oldSize)) {
          result = size - oldSize;
        }
      }
    }
    return result;
  }

  function getVisibleItems() {
    const offsetTop = getOffsetTop();
    const nItemsInViewportWithBuffer = getNItemsInViewportWithBuffer();
    const sliceFrom = offsetTop.offsetTopItemsBuffer;
    const sliceTo = sliceFrom + nItemsInViewportWithBuffer;
    return items.slice(sliceFrom, sliceTo);
  }

  function getViewportItems() {
    const offsetTop = getOffsetTop();
    const offsetBottom = getOffsetBottom();
    const sliceFrom = offsetTop.offsetTopItems;
    const sliceTo = items.length - offsetBottom.offsetBottomItems;
    return items.slice(sliceFrom, sliceTo);
  }

  function getBufferTopItems() {
    const offsetTop = getOffsetTop();
    const { offsetTopItems } = offsetTop;
    const nBuffer = Math.min(offsetTopItems, buffer);
    const topItems = items.slice(0, offsetTopItems);
    return nBuffer > 0 ? topItems.slice(-nBuffer) : [];
  }

  function updateItems(options: IVScrollUpdateOptions = {}) {
    const mapper = (item: IVScrollItem) => item[dataKey];
    const oldIds = items.map(mapper);
    items = options.items!;
    const newIds = items.map(mapper);
    const newIncludeOld = _.difference(oldIds, newIds).length === 0;
    if (!newIncludeOld) {
      // itemSizesById = {}; // FIXME: Use delayed render for VS items
    }
  }

  function update(options: IVScrollUpdateOptions = {}) {
    let scrollTopDiff = 0;
    if (options.items) {
      updateItems(options);
    } else if (options.viewportSize) {
      viewportSize = options.viewportSize;
    } else if (options.headerSlotSize) {
      headerSlotSize = options.headerSlotSize;
    } if (options.footerSlotSize) {
      footerSlotSize = options.footerSlotSize;
    } else {
      if (activeItemId && options.resize) {
        scrollTopDiff = getScrollTopDiff(options.resize);
      }
      scrollTop = (options.scrollTop ?? scrollTop) + scrollTopDiff;
      scrollTop = Math.max(scrollTop, 0);
      if (options.resize) {
        itemSizesById[options.resize.id] = options.resize.size;
      }
    }

    const offsetTop = getOffsetTop();
    const offsetBottom = getOffsetBottom();
    const visibleItems = getVisibleItems();
    const viewportItems = getViewportItems();

    return {
      offsetTop,
      offsetBottom,
      visibleItems,
      scrollTopDiff,
      viewportItems,
      itemSizesById,
    };
  }

  function getItemId(item: IVScrollItem): string | number {
    return item[dataKey];
  }

  function getItemSize(item: IVScrollItem): number {
    const itemId = getItemId(item);
    return itemSizesById[itemId] ?? defaultItemSize;
  }

  function getViewportSize(): number {
    return viewportSize;
  }

  function getItemOffsetTop(id: string): number {
    let result = 0;
    const item = items.find(item => getItemId(item) === id);
    if (item) {
      const index = items.indexOf(item);
      for (let i = 0; i < index; i++) {
        result += getItemSize(items[i]);
      }
    }
    return result;
  }

  function setActiveItemId(id: number | string) {
    activeItemId = id;
  }

  function setScrollTop(value: number) {
    return update({ scrollTop: value });
  }

  function setItemSize(id: number | string, size: number) {
    return update({ resize: { id, size } });
  }

  function setItems(items: IVScrollItem[]) {
    return update({ items });
  }

  function setViewportSize(viewportSize: number) {
    return update({ viewportSize });
  }

  function setHeaderSlotSize(headerSlotSize: number) {
    return update({ headerSlotSize });
  }

  function setFooterSlotSize(headerSlotSize: number) {
    return update({ headerSlotSize });
  }

  return {
    setActiveItemId,
    setItems,
    setItemSize,
    getItemId,
    getItemSize,
    getViewportSize,
    getItemOffsetTop,
    setScrollTop,
    setViewportSize,
    setHeaderSlotSize,
    setFooterSlotSize,
  };
}
